import { useRouter } from '@tanstack/react-router';
import { Moon, Sun } from 'lucide-react';
import type React from 'react';

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '~/components/shadcn/dropdown-menu.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { useKeyboardShortcut } from '~/modules/keyboard/hooks.ts';
import { cn } from '~/utils/cn.ts';
import { saveThemePreference, Theme, useTheme } from '~/utils/theme-utils.ts';

import { CommandBadge } from './navigation-command-palette/command-badge.tsx';

export interface ThemeSwitchProps {
	readonly className?: string;
	readonly command?: string[];
	readonly isDialogOpen: boolean;
	readonly isMobile?: boolean;
	readonly isOpen: boolean;
	readonly setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	readonly showKeyboardNavigation?: boolean;
	readonly textWidth: string;
}

export function ThemeSwitch({
	className,
	command,
	isDialogOpen,
	isMobile = false,
	isOpen,
	setIsOpen,
	showKeyboardNavigation,
	textWidth,
	...props
}: ThemeSwitchProps) {
	const t = useTranslations();
	const router = useRouter();
	const theme = useTheme();

	useKeyboardShortcut({
		callback: () => setIsOpen(true),
		command,
		shouldFire: !isOpen,
	});

	async function handleSelect(themeValue: Theme) {
		await saveThemePreference(themeValue);
		await router.invalidate();
	}

	function handleOpenChange(value: boolean) {
		setIsOpen(value);
	}

	function handleCloseFocus(e: Event) {
		e.preventDefault();
	}

	return (
		<DropdownMenu onOpenChange={handleOpenChange} open={isOpen}>
			<DropdownMenuTrigger asChild>
				<button
					aria-label={t('nav.theme')}
					className={cn('group/link transition-colors duration-500', className, isMobile && 'w-8')}
					title={t('nav.theme')}
					type="button"
					{...props}
				>
					{theme === 'light' ? (
						<Moon className="size-4 shrink-0 rotate-30 transition-all dark:rotate-0" />
					) : (
						<Sun className="size-4 shrink-0 rotate-0 transition-all dark:-rotate-90" />
					)}
					{!isDialogOpen && !isMobile ? (
						<span
							className={cn(
								'text-left text-sm opacity-0',
								textWidth,
								'group-focus-within:opacity-100 group-hover:opacity-100',
							)}
						>
							{t('nav.theme')}
						</span>
					) : null}
					{showKeyboardNavigation ? <CommandBadge command={command} /> : null}
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				className="z-[calc(var(--z-index-dialog)+1)]"
				onCloseAutoFocus={handleCloseFocus}
			>
				<DropdownMenuItem onClick={() => void handleSelect(Theme.LIGHT)}>{t('nav.light')}</DropdownMenuItem>
				<DropdownMenuItem onClick={() => void handleSelect(Theme.DARK)}>{t('nav.dark')}</DropdownMenuItem>
				<DropdownMenuItem onClick={() => void handleSelect(Theme.SYSTEM)}>{t('nav.system')}</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
